/*
 *   File : dashboard.js
 *   Author : https://evoqins.com
 *   Description : Country admin Home page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format, startOfYear } from 'date-fns';
import { useSelector } from 'react-redux';

//Custom Components
import { StatisticCard } from '../../../Component/Cards';
import { DataTableComponent } from '../../../Component/DataTable';
import { CustomSelectBox, DateRangePicker } from '../../../Component/FormElements';
import { DoughnutChart } from '../../../Component/Chart';
import { Icon } from '../../../Component/Icon';
import { APIService } from '../../../Service';
import { CustomTab, Loader, OverLay } from '../../../Component/Others';
import { DownloadButton } from '../../../Component/Buttons';

//Custom style
import styles from "../../../Style/Component/dashboard.module.scss";


const DASHBOARD_FILTER = [
    {
        value: 1,
        label: 'last week',
    },
    {
        value: 2,
        label: 'last month'
    },
    {
        value: 3,
        label: 'last year'
    },
    {
        value: 4,
        label: 'last 5 year'
    },
];
const TOP_SELLING_FILTER = [
    {
        value: 1,
        label: 'By shop',
    },
    {
        value: 2,
        label: 'By sales specialists'
    }
];

const REGISTRATION_FILTER = [
    {
        value: 1,
        label: 'Weekly',
    },
    {
        value: 2,
        label: 'Monthly'
    },
    {
        value: 3,
        label: 'Yearly'
    },
];

const TAB_DATA = [
    {
        id: 1,
        label: "All country"
    },
    {
        id: 2,
        label: "Sales specialists"
    },
];

const CHART_FILTER_TAB = [
    {
        id: 1,
        label: "Active banners"
    },
    {
        id: 2,
        label: "Deactivated banners"
    },
    {
        id: 3,
        label: "Archived banners"
    },
    {
        id: 4,
        label: "Scheduled banners"
    },
];

const Dashboard = () => {

    const today = new Date();

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const parseInceptionDate = (value) => {
        if (!value || typeof value !== 'string') return null;
        const [year, month, day] = value.split('-').map(Number);
        if (!year || !month || !day) return null;
        return new Date(year, month - 1, day);
    };

    const inceptionStart = parseInceptionDate(PROFILE_DATA?.inception_date);

    const navigate = useNavigate();
    const { state } = useLocation();

    const [selectedDashboard, setSelectedDashboard] = useState(DASHBOARD_FILTER[2]);
    const [selectedTopSelling, setSelectedTopSelling] = useState(TOP_SELLING_FILTER[0]);
    const [selectedRegistration, setSelectedRegistration] = useState(REGISTRATION_FILTER[1]);

    const [dashboardData, setDashboardData] = useState({});
    const [loading, setLoading] = useState(true);

    const [topSellingData, setTopSellingData] = useState([]);
    const [topSellingLoading, setTopSellingLoading] = useState(true);

    const [userRegistrationData, setUserRegistrationData] = useState({});
    const [registrationLoading, setRegistrationLoading] = useState(true);

    const [topSellingProductData, setTopSellingProductData] = useState({});
    const [sellingProductLoading, setSellingProductLoading] = useState(true);

    const [educationResourceData, setEducationResourceData] = useState({});
    const [resourceLoading, setResourceLoading] = useState(true);

    const [tabIndex, setTabIndex] = useState(0);


    const [selectedDate, setSelectedDate] = useState([startOfYear(today), today]);
    const [datePresetId, setDatePresetId] = useState(7); // This year

    const [selectedType, setSelectedType] = useState(null);
    const [selectedInnovation, setSelectedInnovation] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const [filterData, setFilterData] = useState({});
    const [filterLoader, setFilterLoader] = useState();

    const [insightData, setInsightData] = useState({});
    const [insightDataLoader, setInsightDataLoader] = useState(false);

    const [downloadLoader, setDownloadLoader] = useState(false);
    const [selectedFinish, setSelectedFinish] = useState(null);

    const [menuIsOpenFinish, setMenuIsOpenFinish] = useState(false);
    const [menuIsOpenInnovation, setMenuIsOpenInnovation] = useState(false);
    const [menuIsOpenCategory, setMenuIsOpenCategory] = useState(false);

    useEffect(() => {
        if (state?.tab_index) {
            setTabIndex(state.tab_index);
        } else {
            setTabIndex(1);
        }
    }, [state]);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
        _getEducationResource();
        _getDashboardFilterData();
    }, []);

    useEffect(() => {
        if (selectedType !== null && tabIndex !== 0) {
            _getTopSellingProduct();
        }
    }, [selectedType, selectedCategory, selectedInnovation, selectedFinish,tabIndex]);

    useEffect(() => {
        if (tabIndex === 2) {
            setInsightDataLoader(true);
            _getInsightData();
        }
    }, [tabIndex]);

    useEffect(() => {
        if (tabIndex !== 0) {
            _getDashboardData();

        }
    }, [tabIndex]);

    useEffect(() => {
        if (tabIndex !== 0) {
            _getTopSellingData();
        }
    }, [selectedTopSelling, tabIndex]);

    useEffect(() => {
        _getUserRegistrationData();
    }, [selectedRegistration]);

    // handle sort top selling table
    const _handleSortTopSelling = (value) => {
        setSelectedTopSelling(value);
        setTopSellingLoading(true);
    };


    // Navigate to Sales invoices with store / specialist filter so admin can review top-seller activity
    const _handleNavigate = (row) => {
        const isStoreView = tabIndex !== 2 && selectedTopSelling?.value === 1;
        const start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        if (isStoreView) {
            if (!row?.store_id) return;
            navigate("/invoices", {
                state: {
                    tab_index: 2,
                    store_id: row.store_id,
                    store_name: row.store_name,
                    start_date,
                    end_date,
                }
            });
            return;
        }

        if (!row?.customer_id) return;
        navigate("/invoices", {
            state: {
                tab_index: 2,
                customer_id: row.customer_id,
                customer_name: row.username,
                start_date,
                end_date,
            }
        });
    };


    // API - Dash board data
    const _getDashboardData = () => {

        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'CA/dashboard/get';
        let data = {
            "filter": tabIndex,
            "start_date": formatted_start_date,
            "end_date": formatted_end_date
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setDashboardData(response.data);
            } else {
            }
            setLoading(false);
        });
    };

    // API - Dash board data
    const _getTopSellingData = () => {

        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'CA/dashboard/get-top-selling';
        let data = {
            "scope": tabIndex,
            "filter": tabIndex === 2 ? 2 : selectedTopSelling.value,
            "limit": 5,
            "start_date": formatted_start_date,
            "end_date": formatted_end_date
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setTopSellingData(response.data.data);
            } else {
                setTopSellingData([]);
            }
            setTopSellingLoading(false);
        });
    };

    // API - Get user registrations
    const _getUserRegistrationData = () => {
        let is_post = true;
        let url = 'CA/dashboard/user-registration-statistic';
        let data = {
            "filter": selectedRegistration.value
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setUserRegistrationData(response.data);
            } else {
            }
            setRegistrationLoading(false);
        });
    };

    // API - Dash board filter data
    const _getDashboardFilterData = () => {
        let is_post = false;
        let url = 'CA/dashboard/filter-data';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                const updated_type = response.data.type.map(item => ({
                    id: item.value,
                    label: item.label
                }));

                const updated_filter_data = {
                    ...response.data,
                    type: updated_type
                };

                setFilterData(updated_filter_data);

                setSelectedType(updated_type[0].id ?? null);
                setSelectedInnovation(response.data.innovation[0] ?? null);
                setSelectedCategory(response.data.category[0] ?? null);
                setSelectedFinish(response.data.finish[0] ?? null);

            } else {
            }
            setFilterLoader(false);
        });
    };


    // API - Get top selling products data
    const _getTopSellingProduct = () => {
        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'CA/dashboard/get-top-selling-product';
        let data = {
            "type": selectedType,        // Optional (1 = All, 2 = Interior, 3 = Exterior)
            "innovation": selectedInnovation?.value,  // Optional (1 = Innovation, 2 = Old)
            "category": selectedCategory?.value, // Optional (1 = Premium, 2 = Medium)
            "finish": selectedFinish?.value,
            "scope": tabIndex,        // Optional (1 = country, 2 = only my assigned users)
            "start_date": formatted_start_date,
            "end_date": formatted_end_date
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setTopSellingProductData(response.data);
            } else {
                setTopSellingProductData({ data: [] });
            }
            setSellingProductLoading(false);
        });
    };

    // API - Get education resource data
    const _getEducationResource = () => {
        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'CA/dashboard/education-resource';
        let data = {
            "start_date": formatted_start_date,
            "end_date": formatted_end_date
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setEducationResourceData(response.data);
            } else {
            }
            setResourceLoading(false);
        });
    };

    const _handleTab = (id) => {
        navigate("/", {
            state: {
                tab_index: id
            }
        })

        setLoading(true);
        // Reset product type filter (All / Interior / Exterior) to default
        setSelectedType(filterData?.type?.[0]?.id ?? 1);
        setSelectedInnovation(filterData?.innovation?.[0] ?? null);
        setSelectedCategory(filterData?.category?.[0] ?? null);
        setSelectedFinish(filterData?.finish?.[0] ?? null);
    };

    const _handleUserDetail = (id) => {
        navigate("/sales-specialist/detail", {
            state: {
                customer_id: id,
                tabIndex: 1
            }
        });
    };

    const _handleChartFilter = (id) => {
        setSelectedType(id);
    };

    // API - Get insight data 
    const _getInsightData = () => {
        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'CA/dashboard/insights';
        let data = {
            "scope": tabIndex,
            "start_date": formatted_start_date,
            "end_date": formatted_end_date
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setInsightData(response.data);
            } else {
            }
            setInsightDataLoader(false);
        });
    };


    // API - Download
    const _download = () => {
        setDownloadLoader(true);

        const formatted_start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd');
        const formatted_end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'CA/dashboard/download';
        let data = {
            "start_date": formatted_start_date,
            "end_date": formatted_end_date
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    // Top selling data table 
    const TABLE_COLUMNS = [
        {
            name: selectedTopSelling.value === 1 ? 'Store' : 'User name',
            selector: row => selectedTopSelling.value === 1 ? row.store_name : row.username,
            width: "500px"
        },
        {
            name: PROFILE_DATA.country === "Egypt" ? 'Total units' : "Total Ltrs",
            selector: row => row.Total_earning || row.total_liters_sold,
            width: "160px"
        },
        {
            name: 'Points earned',
            selector: row => row.points_earned || "-",
            width: "120px"
        },

    ];

    // Top performing data table 
    const TABLE_PERFORMING_COLUMNS = [
        {
            name: 'Specialist name',
            selector: row => row.username,
            cell: row => <>{row.username}</>,
            width: "260px"
        },

        {
            name: 'Stores',
            selector: row => row.store_name || "-",
            cell: row => <>{row.store_name || "-"}</>,
            width: "260px"
        },
        {
            name: PROFILE_DATA.country === "Egypt" ? 'Total units sold' : "Total Ltrs sold",
            selector: row => row.total_liters_sold || "-",
            width: "180px"
        },
        {
            name: PROFILE_DATA.country === "Egypt" ? 'Premium units sold' : "Premium Ltrs sold",
            selector: row => row.premium_liters_sold || "-",
            width: "180px"
        },
        {
            name: 'Points earned',
            selector: row => row.points_earned || "-",
            width: "120px"
        },

    ];

    console.log("topSellingProductData",topSellingProductData);
    

    return (
        <section className='pb-5'>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <div className='d-flex justify-content-space-between align-items-center'>
                    <h1 className='mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                        Overview
                    </h1>
                    <div className='d-flex align-items-center gap-4'>
                        <div className='e-bg-indigo e-border-radius-24 py-1 px-2 d-flex align-items-center gap-1'>
                            {TAB_DATA.map((data, index) => (
                                <p key={index} className={`${tabIndex === data.id ? "e-bg-lavender-blush-light e-border-radius-16  e-text-raisin-black pe-none" : "e-text-white cursor-pointer"} py-1 px-2 mb-0 e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px`}
                                    onClick={() => _handleTab(data.id)}>
                                    {data.label}
                                </p>
                            ))}
                        </div>

                        {/* <div>
                            <CustomSelectBox value={selectedDashboard}
                                options={DASHBOARD_FILTER}
                                type={1}
                                className="e-header-select-box"
                                selectChange={(value) => _handleSortDashboard(value)}
                                isSearchable={false} />
                        </div> */}

                        <DateRangePicker
                            placeholder="Select"
                            calendarWrapperClass="end-0"
                            value={selectedDate}
                            maxDate={new Date}
                            minDate={inceptionStart || undefined}
                            inceptionDate={PROFILE_DATA?.inception_date || null}
                            activePresentId={datePresetId}
                            handleClear={() => setSelectedDate(null)}
                            handleChange={(date) => setSelectedDate(date)}
                            handleApply={() => {
                                setLoading(true);
                                setTopSellingLoading(true);
                                setSellingProductLoading(true);
                                setResourceLoading(true);
                                setInsightDataLoader(true);
                                _getDashboardData();
                                _getTopSellingData();
                                _getTopSellingProduct();
                                _getInsightData();
                                _getEducationResource();
                            }} />

                        {/* <DownloadButton loader={downloadLoader}
                            disabled={loading}
                            onClick={_download} /> */}
                    </div>

                </div>
            </div>
            <div className='padding-52px-lr margin-neg-66px-top'>
                {/* Statistic Card  */}
                <div className={`${PROFILE_DATA.country === "Egypt" || tabIndex === 2 ? "row-cols-md-4" : "row-cols-xl-5 row-cols-lg-4 row-cols-md-4 "} row-cols-2 row g-3 row-gap-3 `}>
                    {tabIndex === 2 ?
                        <>
                            <div className='col m-0 '>
                                <StatisticCard icon={require("../../../Assets/Image/CountryAdmin/total-registered.svg").default}
                                    loader={loading}
                                    quantity={dashboardData.total_registered}
                                    title={"Total registered"}
                                    className="e-box-shadow-business-stats e-filter-none e-border-radius-16" />
                            </div>
                            <div className='col m-0 '>
                                <StatisticCard icon={require("../../../Assets/Image/CountryAdmin/point-redeemed.svg").default}
                                    loader={loading}
                                    quantity={dashboardData.points_redeemed}
                                    title={"Points redeemed so far"}
                                    className="e-box-shadow-business-stats e-filter-none e-border-radius-16" />
                            </div>

                            <div className='col m-0 '>
                                <StatisticCard icon={require("../../../Assets/Image/CountryAdmin/coupon-available.svg").default}
                                    loader={loading}
                                    quantity={dashboardData.coupons_count}
                                    title={"Total coupons available"}
                                    className="e-box-shadow-business-stats e-filter-none e-border-radius-16" />
                            </div>
                        </>
                        :
                        <>
                            <div className='col m-0 '>
                                <StatisticCard icon={require("../../../Assets/Image/CountryAdmin/total-sales.svg").default}
                                    loader={loading}
                                    quantity={dashboardData.total_sale}
                                    title={"Total volume"}
                                    className="e-box-shadow-business-stats e-filter-none e-border-radius-16" />
                            </div>
                            <div className='col m-0 '>
                                <StatisticCard icon={require("../../../Assets/Image/CountryAdmin/premium-sales.svg").default}
                                    loader={loading}
                                    quantity={dashboardData.total_premium_sale}
                                    title={"Premium volume"}
                                    className="e-box-shadow-business-stats e-filter-none e-border-radius-16" />
                            </div>

                            {PROFILE_DATA.country !== "Egypt" &&
                                <div className='col m-0 '>
                                    <StatisticCard icon={require("../../../Assets/Image/CountryAdmin/sold.svg").default}
                                        loader={loading}
                                        quantity={dashboardData.innovations_volume}
                                        title={PROFILE_DATA.country === "Egypt" ? "Product units sold" : "Innovations volume"}
                                        className="e-box-shadow-business-stats e-filter-none e-border-radius-16" />
                                </div>
                            }

                            <div className='col m-0 '>
                                <StatisticCard icon={require("../../../Assets/Image/CountryAdmin/users.svg").default}
                                    loader={loading}
                                    quantity={dashboardData.total_users}
                                    title={"Total users"}
                                    className="e-box-shadow-business-stats e-filter-none e-border-radius-16" />
                            </div>


                            <div className='col m-0 '>
                                <StatisticCard icon={require("../../../Assets/Image/CountryAdmin/transaction.svg").default}
                                    loader={loading}
                                    quantity={dashboardData.transactions}
                                    title={"Transactions"}
                                    className="e-box-shadow-business-stats e-filter-none e-border-radius-16" />
                            </div>
                        </>
                    }
                </div>

                {tabIndex === 1 ?
                    <>
                        <div className='row padding-32px-top'>
                            {/* Top Sales Specialists data table */}
                            <div className='col-12'>
                                <div className='d-flex flex-column h-100'>
                                    <div className='flex-grow-1 e-bg-white e-leader-board-box-shadow py-4 e-border-radius-16'>
                                        <div className='d-flex align-items-center justify-content-between px-4'>
                                            <h4 className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                                                Top seller
                                            </h4>
                                            <div >
                                                <CustomSelectBox value={selectedTopSelling}
                                                    options={TOP_SELLING_FILTER}
                                                    type={1}
                                                    menuWidth="max-content"
                                                    isSearchable={false}
                                                    selectChange={(value) => _handleSortTopSelling(value)} />
                                            </div>
                                        </div>
                                        {topSellingLoading ?
                                            <Loader height="min-h-362px" /> ///loader
                                            :
                                            // Top Sales Specialists data table 
                                            <>
                                                {topSellingData.length !== 0 ?
                                                    <div className='pt-3 e-leader-board-table'>
                                                        <DataTableComponent columns={TABLE_COLUMNS}
                                                            data={topSellingData}
                                                            rowClick={(row) => _handleNavigate(row)} />

                                                        <div className={`padding-14px-top  text-center `}>
                                                            <span className='e-text-indigo e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-20px text-decoration-hover-underline cursor-pointer'
                                                                onClick={() => {
                                                                    navigate("/top-selling", {
                                                                        state: {
                                                                            filter_id: selectedTopSelling?.value || null
                                                                        }
                                                                    })
                                                                }}>
                                                                See more
                                                                <Icon icon={"arrow-left"}
                                                                    className="ms-2 transform-rotate-180"
                                                                    size={16} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className='d-flex align-items-center flex column h-100 justify-content-center py-5' style={{ minHeight: "362px" }}>
                                                        <p className='mb-0 e-alt-font-noto-sans e-font-16 line-height-22px e-text-raisin-black e-font-weight-500'>
                                                            No data found
                                                        </p>
                                                    </div>
                                                }
                                            </>

                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='row padding-32px-top row-gap-4 '>
                            <div className='col-lg-7 e-col-7 col-12 pe-1'>
                                <div className='d-flex flex-column h-100'>
                                    <div className={` e-box-shadow-business-stats e-chart-scroll e-bg-azureish-white p-4 e-border-radius-16 flex-grow-1`}>
                                        <div className='d-flex align-items-center justify-content-between gap-2'>
                                            <h4 className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px text-nowrap'>
                                                Top selling products
                                            </h4>

                                            <div className='d-flex gap-12px align-items-center'>
                                                {filterData?.type?.map((filter) => (
                                                    <CustomTab key={filter.id}
                                                        data={filter}
                                                        padding="py-2 padding-12px-lr"
                                                        tabIndex={selectedType}
                                                        handleTabChange={_handleChartFilter} />
                                                ))}
                                            </div>
                                        </div>

                                        <div className='d-flex align-items-center gap-0-6px pt-3' >
                                            <OverLay isOpen={menuIsOpenFinish || menuIsOpenInnovation || menuIsOpenCategory}
                                                className={"z-1"}
                                                onClose={() => {
                                                    if (menuIsOpenFinish) {
                                                        setMenuIsOpenFinish(false);
                                                    } else if (menuIsOpenInnovation) {
                                                        setMenuIsOpenInnovation(false);
                                                    } else {
                                                        setMenuIsOpenCategory(false);
                                                    }
                                                }} />

                                            <div className='position-relative cursor-pointer '
                                                onClick={() => setMenuIsOpenFinish(prev => !prev)}>

                                                <CustomSelectBox value={selectedFinish}
                                                    options={filterData.finish}
                                                    loader={filterLoader}
                                                    borderColor="213366"
                                                    padding="4px 0 4px 4px"
                                                    menuWidth="max-content"
                                                    type={1}
                                                    width="82px"
                                                    fontSize="12px"
                                                    isSearchable={false}
                                                    menuIsOpen={menuIsOpenFinish}
                                                    selectChange={(value) => setSelectedFinish(value)} />

                                                {selectedFinish?.value === null &&
                                                    <p className='position-absolute top-4px left-6px  e-border-radius-16 mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-11 e-font-weight-600 line-height-24px e-bg-azureish-white'>
                                                        By finish
                                                    </p>
                                                }
                                            </div>

                                            <div className='position-relative cursor-pointer '
                                                onClick={() => setMenuIsOpenInnovation(prev => !prev)}>
                                                <CustomSelectBox value={selectedInnovation}
                                                    options={filterData.innovation}
                                                    loader={filterLoader}
                                                    borderColor="213366"
                                                    padding="4px 0 4px 4px"
                                                    menuWidth="max-content"
                                                    type={1}
                                                    width="82px"
                                                    fontSize="12px"
                                                    isSearchable={false}
                                                    menuIsOpen={menuIsOpenInnovation}
                                                    selectChange={(value) => setSelectedInnovation(value)} />

                                                {selectedInnovation?.value === null &&
                                                    <p className='position-absolute top-4px left-4px  e-border-radius-16 mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-11 e-font-weight-600 line-height-24px e-bg-azureish-white'>
                                                        Innovation
                                                    </p>
                                                }
                                            </div>


                                            <div className='position-relative cursor-pointer '
                                                onClick={() => setMenuIsOpenCategory(prev => !prev)}>
                                                <CustomSelectBox value={selectedCategory}
                                                    options={filterData.category}
                                                    loader={filterLoader}
                                                    padding="4px 0 4px 4px"
                                                    borderColor="213366"
                                                    menuWidth="max-content"
                                                    type={1}
                                                    fontSize="12px"
                                                    width="86px"
                                                    menuIsOpen={menuIsOpenCategory}
                                                    isSearchable={false}
                                                    selectChange={(value) => setSelectedCategory(value)} />

                                                {selectedCategory?.value === null &&
                                                    <p className='position-absolute top-4px left-4px  e-border-radius-16 mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-11 e-font-weight-600 line-height-24px e-bg-azureish-white'>
                                                        By category
                                                    </p>
                                                }
                                            </div>
                                        </div>

                                        {sellingProductLoading ?
                                            <Loader className="h-100" /> ///loader
                                            :
                                            <>
                                                {topSellingProductData?.data?.length !== 0 ?
                                                    <div className='row pt-3 mt-1'>
                                                        <div className='col-7 position-relative'>
                                                            <div className='w-max-content'>
                                                                <DoughnutChart label={topSellingProductData.label}
                                                                    data={topSellingProductData.data}
                                                                    totalProduct={topSellingProductData.total_product} />
                                                            </div>
                                                        </div>

                                                        <div className={`${styles.e_interior_paint} col-5 ps-0 my-auto`}>
                                                            <div className={`${styles.e_top_selling_scroll} h-210 overflow-auto`}>
                                                                {topSellingProductData.data[0] &&
                                                                    <div className='pb-3'>
                                                                        <div className='d-flex align-items-start gap-2 flex-nowrap'>
                                                                            <div className='e-bg-spanish-yellow h-12px w-12px rounded-circle flex-shrink-0'></div>
                                                                            <p className={`${styles.first} mb-0 w-max-content  text-nowrap e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-14px`}>
                                                                                {topSellingProductData.label[0]}
                                                                            </p>
                                                                        </div>
                                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-28px'>
                                                                            {topSellingProductData.data[0]} <span className='e-font-14 e-font-weight-400'>{PROFILE_DATA.country === "Egypt" ? "Units" : "Ltrs"}</span>
                                                                        </p>
                                                                    </div>
                                                                }

                                                                {topSellingProductData.data[1] &&
                                                                    <div className='pb-3'>
                                                                        <div className='d-flex align-items-start gap-2 flex-nowrap'>
                                                                            <div className='e-bg-lust h-12px w-12px rounded-circle flex-shrink-0'></div>
                                                                            <p className={`${styles.first} mb-0 w-max-content  text-nowrap e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-14px`}>
                                                                                {topSellingProductData.label[1]}
                                                                            </p>
                                                                        </div>

                                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-28px'>
                                                                            {topSellingProductData.data[1]} <span className='e-font-14 e-font-weight-400'>{PROFILE_DATA.country === "Egypt" ? "Units" : "Ltrs"}</span>
                                                                        </p>
                                                                    </div>
                                                                }

                                                                {topSellingProductData.data[2] &&
                                                                    <div>
                                                                        <div className='d-flex align-items-start gap-2 flex-nowrap'>
                                                                            <div className='e-bg-indigo h-12px w-12px rounded-circle flex-shrink-0'></div>
                                                                            <p className={`${styles.first} mb-0 w-max-content  text-nowrap e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-14px`}>
                                                                                {topSellingProductData.label[2]}
                                                                            </p>
                                                                        </div>
                                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-22px'>
                                                                            {topSellingProductData.data[2]} <span className='e-font-14 e-font-weight-400'>{PROFILE_DATA.country === "Egypt" ? "Units" : "Ltrs"}</span>
                                                                        </p>
                                                                    </div>
                                                                }
                                                            </div>
                                                        </div>
                                                        <div className={`padding-14px-top  text-center `}>
                                                            <span className='e-text-indigo e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-20px text-decoration-hover-underline cursor-pointer'
                                                                onClick={() => {
                                                                    navigate("/product-detail", {
                                                                        state: {
                                                                            scope: tabIndex,
                                                                            start_date: selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd'),
                                                                            end_date: selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd'),
                                                                        }
                                                                    })
                                                                }}>
                                                                See more
                                                                <Icon icon={"arrow-left"}
                                                                    className="ms-2 transform-rotate-180"
                                                                    size={16} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className='d-flex align-items-center flex column h-100 justify-content-center'>
                                                        <p className='mb-0 e-alt-font-noto-sans e-font-16 line-height-22px e-text-raisin-black e-font-weight-500'>
                                                            No data found
                                                        </p>
                                                    </div>
                                                }
                                            </>

                                        }
                                    </div>
                                </div>
                            </div>

                            {/* Resources summary */}
                            <div className='col-lg-5 e-col-5 col-12'>
                                <div className='d-flex flex-column h-100'>
                                    <div className='e-bg-white e-leader-board-box-shadow p-4 e-border-radius-16 flex-grow-1'>
                                        <h4 className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                                            Resources summary
                                        </h4>
                                        <div className='pt-3'>
                                            {resourceLoading ?
                                                <Loader className="h-100" /> ///loader
                                                :
                                                <>
                                                    {/* Courses */}
                                                    <div className={`mb-3 padding-28px-tb padding-12px-lr d-flex gap-12px align-items-center  e-border-radius-8  e-bg-azureish-white-light`}>
                                                        <Icon icon="active-courses"
                                                            size={24} />
                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-22px'>
                                                            <span className='pe-1 e-font-weight-400'>
                                                                {educationResourceData.no_of_active_courses === 1 ? "No of active course:" : "No of active courses:"}
                                                            </span>
                                                            {educationResourceData.no_of_active_courses}
                                                        </p>
                                                    </div>

                                                    {/* Video snippet */}
                                                    <div className={`mb-3 padding-28px-tb padding-12px-lr d-flex gap-12px align-items-center  e-border-radius-8 e-bg-lavender-blush`}
                                                    >
                                                        <Icon icon="total-courses"
                                                            size={24} />
                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-22px'>
                                                            <span className='pe-1 e-font-weight-400'>
                                                                {educationResourceData.total_course_enrollments === 1 ? "Total course enrollment:" : "Total course enrollments:"}
                                                            </span>
                                                            {educationResourceData.total_course_enrollments}
                                                        </p>
                                                    </div>

                                                    {/* Flash quizzes */}
                                                    <div className={`padding-28px-tb padding-12px-lr d-flex gap-12px align-items-center  e-border-radius-8 e-bg-alice-blue-light`}
                                                    >
                                                        <Icon icon="completed-courses"
                                                            size={24} />
                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-22px'>
                                                            <span className='pe-1 e-font-weight-400'>
                                                                {"Users completed course assessment:"}
                                                            </span>
                                                            {educationResourceData.users_completed_course_assessment}
                                                        </p>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                    :
                    <>

                        <div className='row padding-32px-top row-gap-4 '>
                            <div className='col-lg-7 e-col-7 col-12 pe-1'>
                                <div className='d-flex flex-column h-100'>
                                    <div className={` e-box-shadow-business-stats e-chart-scroll e-bg-azureish-white p-4 e-border-radius-16 flex-grow-1`}>
                                        <div className='d-flex align-items-center justify-content-between gap-2'>
                                            <h4 className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px text-nowrap'>
                                                Top selling products
                                            </h4>

                                            <div className='d-flex gap-12px align-items-center '>
                                                {filterData?.type?.map((filter) => (
                                                    <CustomTab key={filter.id}
                                                        data={filter}
                                                        padding="py-2 padding-12px-lr"
                                                        tabIndex={selectedType}
                                                        handleTabChange={_handleChartFilter} />
                                                ))}
                                            </div>
                                        </div>

                                        <div className='d-flex align-items-center gap-0-6px pt-3' >
                                            <OverLay isOpen={menuIsOpenFinish || menuIsOpenInnovation || menuIsOpenCategory}
                                                className={"z-1"}
                                                onClose={() => {
                                                    if (menuIsOpenFinish) {
                                                        setMenuIsOpenFinish(false);
                                                    } else if (menuIsOpenInnovation) {
                                                        setMenuIsOpenInnovation(false);
                                                    } else {
                                                        setMenuIsOpenCategory(false);
                                                    }
                                                }} />

                                            <div className='position-relative cursor-pointer '
                                                onClick={() => setMenuIsOpenFinish(prev => !prev)}>

                                                <CustomSelectBox value={selectedFinish}
                                                    options={filterData.finish}
                                                    loader={filterLoader}
                                                    borderColor="213366"
                                                    padding="4px 0 4px 4px"
                                                    menuWidth="max-content"
                                                    type={1}
                                                    width="82px"
                                                    fontSize="12px"
                                                    isSearchable={false}
                                                    menuIsOpen={menuIsOpenFinish}
                                                    selectChange={(value) => setSelectedFinish(value)} />

                                                {selectedFinish?.value === null &&
                                                    <p className='position-absolute top-4px left-6px  e-border-radius-16 mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-11 e-font-weight-600 line-height-24px e-bg-azureish-white'>
                                                        By finish
                                                    </p>
                                                }
                                            </div>

                                            <div className='position-relative cursor-pointer '
                                                onClick={() => setMenuIsOpenInnovation(prev => !prev)}>
                                                <CustomSelectBox value={selectedInnovation}
                                                    options={filterData.innovation}
                                                    loader={filterLoader}
                                                    borderColor="213366"
                                                    padding="4px 0 4px 4px"
                                                    menuWidth="max-content"
                                                    type={1}
                                                    width="82px"
                                                    fontSize="12px"
                                                    isSearchable={false}
                                                    menuIsOpen={menuIsOpenInnovation}
                                                    selectChange={(value) => setSelectedInnovation(value)} />

                                                {selectedInnovation?.value === null &&
                                                    <p className='position-absolute top-4px left-4px  e-border-radius-16 mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-11 e-font-weight-600 line-height-24px e-bg-azureish-white'>
                                                        Innovation
                                                    </p>
                                                }
                                            </div>


                                            <div className='position-relative cursor-pointer '
                                                onClick={() => setMenuIsOpenCategory(prev => !prev)}>
                                                <CustomSelectBox value={selectedCategory}
                                                    options={filterData.category}
                                                    loader={filterLoader}
                                                    padding="4px 0 4px 4px"
                                                    borderColor="213366"
                                                    menuWidth="max-content"
                                                    type={1}
                                                    fontSize="12px"
                                                    width="86px"
                                                    menuIsOpen={menuIsOpenCategory}
                                                    isSearchable={false}
                                                    selectChange={(value) => setSelectedCategory(value)} />

                                                {selectedCategory?.value === null &&
                                                    <p className='position-absolute top-4px left-4px  e-border-radius-16 mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-11 e-font-weight-600 line-height-24px e-bg-azureish-white'>
                                                        By category
                                                    </p>
                                                }
                                            </div>
                                        </div>



                                        {sellingProductLoading ?
                                            <Loader className="h-100" />
                                            :
                                            <>
                                                {topSellingProductData?.data?.length !== 0 ?
                                                    <div className='row pt-3 mt-1'>
                                                        <div className='col-7 position-relative'>
                                                            <div className='w-max-content'>
                                                                <DoughnutChart label={topSellingProductData.label}
                                                                    data={topSellingProductData.data}
                                                                    totalProduct={topSellingProductData.total_product} />
                                                            </div>
                                                        </div>

                                                        <div className={`${styles.e_interior_paint} col-5 ps-0 my-auto`}>
                                                            <div className={`${styles.e_top_selling_scroll} h-210 overflow-auto`}>
                                                                {topSellingProductData.data[0] &&
                                                                    <div className='pb-3'>
                                                                        <div className='d-flex align-items-start gap-2 flex-nowrap'>
                                                                            <div className='e-bg-spanish-yellow h-12px w-12px rounded-circle flex-shrink-0'></div>
                                                                            <p className={`${styles.first} mb-0 w-max-content  text-nowrap e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-14px`}>
                                                                                {topSellingProductData.label[0]}
                                                                            </p>
                                                                        </div>
                                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-28px'>
                                                                            {topSellingProductData.data[0]} <span className='e-font-14 e-font-weight-400'>{PROFILE_DATA.country === "Egypt" ? "Units" : "Ltrs"}</span>
                                                                        </p>
                                                                    </div>
                                                                }

                                                                {topSellingProductData.data[1] &&
                                                                    <div className='pb-3'>
                                                                        <div className='d-flex align-items-start gap-2 flex-nowrap'>
                                                                            <div className='e-bg-lust h-12px w-12px rounded-circle flex-shrink-0'></div>
                                                                            <p className={`${styles.first} mb-0 w-max-content  text-nowrap e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-14px`}>
                                                                                {topSellingProductData.label[1]}
                                                                            </p>
                                                                        </div>

                                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-28px'>
                                                                            {topSellingProductData.data[1]} <span className='e-font-14 e-font-weight-400'>{PROFILE_DATA.country === "Egypt" ? "Units" : "Ltrs"}</span>
                                                                        </p>
                                                                    </div>
                                                                }

                                                                {topSellingProductData.data[2] &&
                                                                    <div>
                                                                        <div className='d-flex align-items-start gap-2 flex-nowrap'>
                                                                            <div className='e-bg-indigo h-12px w-12px rounded-circle flex-shrink-0'></div>
                                                                            <p className={`${styles.first} mb-0 w-max-content  text-nowrap e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-14px`}>
                                                                                {topSellingProductData.label[2]}
                                                                            </p>
                                                                        </div>
                                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-22px'>
                                                                            {topSellingProductData.data[2]} <span className='e-font-14 e-font-weight-400'>{PROFILE_DATA.country === "Egypt" ? "Units" : "Ltrs"}</span>
                                                                        </p>
                                                                    </div>
                                                                }
                                                            </div>
                                                        </div>
                                                        <div className={`padding-14px-top  text-center `}>
                                                            <span className='e-text-indigo e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-20px text-decoration-hover-underline cursor-pointer'
                                                                onClick={() => {
                                                                    navigate("/product-detail", {
                                                                        state: {
                                                                            scope: tabIndex,
                                                                            start_date: selectedDate === null ? null : format(new Date(selectedDate[0]), 'yyyy-MM-dd'),
                                                                            end_date: selectedDate === null ? null : format(new Date(selectedDate[1]), 'yyyy-MM-dd'),
                                                                        }
                                                                    })
                                                                }}>
                                                                See more
                                                                <Icon icon={"arrow-left"}
                                                                    className="ms-2 transform-rotate-180"
                                                                    size={16} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className='d-flex align-items-center flex column h-100 justify-content-center'>
                                                        <p className='mb-0 e-alt-font-noto-sans e-font-16 line-height-22px e-text-raisin-black e-font-weight-500'>
                                                            No data found
                                                        </p>
                                                    </div>
                                                }
                                            </>

                                        }
                                    </div>
                                </div>
                            </div>

                            {/* Insights */}
                            <div className='col-lg-5 e-col-5 col-12'>
                                <div className='d-flex flex-column h-100'>
                                    <div className='e-bg-white e-leader-board-box-shadow p-4 e-border-radius-16 flex-grow-1'>
                                        <h4 className='mb-2 e-text-raisin-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                                            Insights
                                        </h4>
                                        <p className='mb-0 e-text-funky-gray e-alt-font-noto-sans e-font-14 e-font-weight-500 line-height-20px'>
                                            Current Month • Based on litres sold
                                        </p>
                                        <div className='pt-3'>
                                            {insightDataLoader ?
                                                <Loader className="h-100" /> ///loader
                                                :
                                                <>
                                                    {/* Courses */}
                                                    <div className={`mb-3 py-4 padding-12px-lr d-flex gap-12px align-items-center  e-border-radius-8 cursor-pointer e-bg-azureish-white-light`}
                                                        onClick={() => _handleUserDetail(insightData?.top_performer?.customer_id)} >
                                                        <Icon icon="top-performer"
                                                            size={24} />
                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-22px'>
                                                            <span className='pe-1 e-font-weight-400'>
                                                                Top performer
                                                            </span>
                                                            {insightData?.top_performer?.name || "-"}
                                                        </p>
                                                    </div>

                                                    {/* Video snippet */}
                                                    <div className={`mb-3 py-4 padding-12px-lr d-flex gap-12px align-items-center  e-border-radius-8 cursor-pointer e-bg-lavender-blush`}
                                                        onClick={() => _handleUserDetail(insightData?.premium_focused?.customer_id)}>
                                                        <Icon icon="premium-focused"
                                                            size={24} />
                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-22px'>
                                                            <span className='pe-1 e-font-weight-400'>
                                                                Premium focused
                                                            </span>
                                                            {insightData?.premium_focused?.name || "-"}
                                                        </p>
                                                    </div>

                                                    {/* Flash quizzes */}
                                                    <div className={`py-4 padding-12px-lr d-flex gap-12px align-items-center  e-border-radius-8 cursor-pointer e-bg-alice-blue-light`}
                                                        onClick={() => _handleUserDetail(insightData?.fastest_growth?.customer_id)} >
                                                        <Icon icon="fatest-growth"
                                                            size={24} />
                                                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-16 e-font-weight-600 line-height-22px'>
                                                            <span className='pe-1 e-font-weight-400'>
                                                                Fastest growth
                                                            </span>
                                                            {insightData?.fastest_growth?.name || "-"}
                                                        </p>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='row padding-32px-top'>
                            <div className='col-12'>
                                <div className='d-flex flex-column h-100'>
                                    <div className='flex-grow-1 e-bg-white e-leader-board-box-shadow py-4 e-border-radius-16'>
                                        <div className='d-flex align-items-center justify-content-between px-4'>
                                            <h4 className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-20 e-font-weight-500 line-height-28px'>
                                                Top performers
                                            </h4>
                                        </div>
                                        {topSellingLoading ?
                                            <Loader height="min-h-362px" />
                                            :
                                            // Top Sales Specialists data table 
                                            <>
                                                {topSellingData.length !== 0 ?
                                                    <div className='pt-3 e-leader-board-table'>
                                                        <DataTableComponent columns={TABLE_PERFORMING_COLUMNS}
                                                            data={topSellingData}
                                                            rowClick={(row) => _handleNavigate(row)} />


                                                        <div className={`padding-14px-top  text-center `}>
                                                            <span className='e-text-indigo e-alt-font-noto-sans e-font-14 e-font-weight-600 line-height-20px text-decoration-hover-underline cursor-pointer'
                                                                onClick={() => {
                                                                    navigate("/sales-specialist");
                                                                }}>
                                                                See more
                                                                <Icon icon={"arrow-left"}
                                                                    className="ms-2 transform-rotate-180"
                                                                    size={16} />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    :
                                                    <div className='d-flex align-items-center flex column h-100 justify-content-center py-5' style={{ minHeight: "362px" }}>
                                                        <p className='mb-0 e-alt-font-noto-sans e-font-16 line-height-22px e-text-raisin-black e-font-weight-500'>
                                                            No data found
                                                        </p>
                                                    </div>
                                                }
                                            </>

                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>
        </section >
    )
}

export default Dashboard